import { Link, useLocation, useNavigate } from "react-router-dom";
import { LogOut, Shield } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { Toaster } from "../components/ui/sonner";

export default function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const isAdmin = user && user.role === "admin";

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="app-layout">
      <header className="border-b border-[#E5E5E5] bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-5 flex items-center justify-between gap-4">
          <Link
            to={isAdmin ? "/admin" : "/"}
            className="flex items-center gap-3"
            data-testid="brand-link"
          >
            <img
              src="/logo-mark.png"
              alt="InvoiceIQ"
              className="w-9 h-9 rounded-sm"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
            <div>
              <div className="font-display text-[1.05rem] font-bold leading-none">
                InvoiceIQ
              </div>
              <div className="eyebrow mt-1">Smarter reconciliation</div>
            </div>
          </Link>
          <nav className="flex items-center gap-2 text-sm">
            {isAdmin && (
              <Link
                to="/admin"
                className={`px-3 py-1.5 rounded-md transition inline-flex items-center gap-1.5 ${
                  location.pathname === "/admin"
                    ? "bg-[#0A0A0A] text-white"
                    : "text-[#525252] hover:bg-[#FAFAFA]"
                }`}
                data-testid="nav-admin"
              >
                <Shield className="w-3.5 h-3.5" /> Admin
              </Link>
            )}
            <Link
              to="/"
              className={`px-3 py-1.5 rounded-md transition ${
                location.pathname === "/"
                  ? "bg-[#0A0A0A] text-white"
                  : "text-[#525252] hover:bg-[#FAFAFA]"
              }`}
              data-testid="nav-reports"
            >
              Reports
            </Link>
            {user && user.email && (
              <div
                className="hidden md:flex items-center gap-2 ml-3 pl-3 border-l border-[#E5E5E5]"
                data-testid="user-chip"
              >
                <div className="text-right leading-tight">
                  <div className="text-[12px] font-medium">
                    {user.store_name || user.email}
                  </div>
                  <div className="text-[10px] text-[#525252] font-mono-tabular">
                    {user.store_number ? `#${user.store_number}` : user.email}
                  </div>
                </div>
                <button
                  onClick={handleLogout}
                  className="p-1.5 rounded-sm hover:bg-[#FFF0F0] text-[#525252] hover:text-[#DC2626]"
                  data-testid="logout-btn"
                  title="Sign out"
                  aria-label="Sign out"
                >
                  <LogOut className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </nav>
        </div>
      </header>
      <main className="flex-1 max-w-[1400px] mx-auto w-full px-6 md:px-10 py-8 md:py-10">
        {children}
      </main>
      <footer className="border-t border-[#E5E5E5] bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-4 text-[11px] text-[#525252] flex items-center justify-between gap-4 flex-wrap">
          <span className="max-w-3xl leading-relaxed">
            This product is just a guide to help UPS Store Owners to reconcile
            and audit their CMS Reports and Invoices. Not affiliated to UPS or
            The UPS Store. Just to be used for information purposes only.
          </span>
          <span className="font-mono-tabular shrink-0">v1.0</span>
        </div>
      </footer>
      <Toaster richColors closeButton position="top-right" />
    </div>
  );
}
